// ============================================================
// BATTLEFIELD SOLIDS - one parametric primitive, tessellated.
//
// Crates, walls, pillars, hills, cones and cylinders are all the same
// shape with different numbers: a ring of `sides` vertices at the base,
// another (possibly collapsed to a point) at the top. Everything the map
// contains is therefore CONVEX, which is what lets line of sight be a
// single plane-clipping test rather than a mesh intersection.
//
// Pure. Zero React dependency, zero renderer knowledge.
// ============================================================
import type { Cover } from '../../types';
import type { FaceKind, Geometry, Solid, Vec3 } from './types';

const DEFAULT_COVER: Cover = { level: 'none', ap: 0 };

/**
 * Monotonic id counter - deliberately NOT Math.random(), for the reason
 * spelled out in `engine/roster/factories.ts`: the E2E harness stubs
 * Math.random() with a dice sequence, and stealing a die from it shifts
 * every later roll.
 */
let idCounter = 0;
function nextSolidId(): string {
  idCounter += 1;
  return `sol-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

const DEG = Math.PI / 180;
/** Anything under this is noise from trigonometry, not a real dimension. */
const EPSILON = 1e-9;

/**
 * A 1-metre box on the origin, inert in every gameplay sense.
 *
 * Build solids through here rather than as object literals: the defaults
 * are what keep new fields from silently arriving as `undefined`, which
 * Firestore rejects outright (gotchas.md, v6.45.0).
 */
export function makeSolid(overrides: Partial<Solid> = {}): Solid {
  return {
    id: nextSolidId(),
    sides: 4,
    radiusBottom: 0.5,
    radiusTop: 0.5,
    scaleX: 1,
    scaleZ: 1,
    height: 1,
    rotation: 0,
    position: { x: 0, y: 0, z: 0 },
    cover: { ...DEFAULT_COVER },
    terrain: 'normal',
    opaque: false,
    ...overrides,
    // Guard the nested object: a caller spreading a partial must not end
    // up sharing one cover record between two solids.
    ...(overrides.cover ? { cover: { ...overrides.cover } } : {}),
    ...(overrides.position ? { position: { ...overrides.position } } : {}),
  };
}

/** Where a solid's rings go: turned, centred and stretched in its own frame. */
interface Placement {
  rotationDeg: number;
  centre: Vec3;
  scaleX: number;
  scaleZ: number;
}

/**
 * One ring of `sides` points at height `y`: generate on the circle,
 * stretch, then turn, then place.
 *
 * `apothem` is the axis-to-face distance (see Solid.radiusBottom); the
 * vertices sit further out, on the circumradius. The scale is applied in
 * the solid's OWN frame, so a stretched wall rotates as a wall rather
 * than shearing.
 */
function ring(sides: number, apothem: number, y: number, { rotationDeg, centre, scaleX, scaleZ }: Placement): Vec3[] {
  const step = (2 * Math.PI) / sides;
  const r = apothem / Math.cos(step / 2);
  const rot = rotationDeg * DEG;
  const cosR = Math.cos(rot);
  const sinR = Math.sin(rot);
  const out: Vec3[] = [];
  for (let i = 0; i < sides; i++) {
    // The half-step offset puts a FLAT face towards +x and +z on an
    // even-sided shape, so a 4-gon reads as a grid-aligned box rather
    // than as a diamond.
    const a = (i + 0.5) * step;
    const lx = r * Math.cos(a) * scaleX;
    const lz = r * Math.sin(a) * scaleZ;
    out.push({ x: centre.x + lx * cosR - lz * sinR, y, z: centre.z + lx * sinR + lz * cosR });
  }
  return out;
}

export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

/**
 * How see-through a solid is: its own setting, else the older `opaque` flag
 * (opaque when it blocks sight, transparent when not). ONE rule for what is
 * drawn as opaque (the display) and what blocks sight (lineOfSight).
 */
export function solidTransparency(solid: Pick<Solid, 'transparency' | 'opaque'>): 0 | 1 | 2 {
  return solid.transparency ?? (solid.opaque ? 0 : 2);
}

function normalise(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z);
  if (len < EPSILON) return { x: 0, y: 0, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/** Mean of the given vertices. */
export function centroid(points: readonly Vec3[]): Vec3 {
  const n = points.length || 1;
  return points.reduce((acc, p) => ({ x: acc.x + p.x / n, y: acc.y + p.y / n, z: acc.z + p.z / n }), {
    x: 0,
    y: 0,
    z: 0,
  });
}

/**
 * The outward normal of a polygon, disambiguated by pushing it away from
 * an interior reference point. Newell's method rather than one cross
 * product, so a near-degenerate triangle in the ring does not decide the
 * orientation of the whole face.
 */
function faceNormal(points: Vec3[], interior: Vec3): Vec3 {
  let n: Vec3 = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    n = {
      x: n.x + (a.y - b.y) * (a.z + b.z),
      y: n.y + (a.z - b.z) * (a.x + b.x),
      z: n.z + (a.x - b.x) * (a.y + b.y),
    };
  }
  const unit = normalise(n);
  return dot(unit, sub(centroid(points), interior)) < 0 ? { x: -unit.x, y: -unit.y, z: -unit.z } : unit;
}

/**
 * Tessellated geometry, memoised on the solid's identity.
 *
 * Solids are immutable - every edit produces a new object, which is the
 * rule the whole battlefield is built on - so identity is a safe key and
 * a `WeakMap` lets a discarded solid take its geometry with it. The same
 * pattern `meshVoxels` and `meshExcavation` already use.
 *
 * This is not a micro-optimisation. NOTHING cached this until v7.33.0,
 * and everything that walks the map calls it: sight, cover, settling and
 * - since the step-limited routing arrived - the flood fill, which asked
 * for the surface under every neighbour of every tile it visited.
 * Choosing "Run" on an ordinary map rebuilt every solid on it tens of
 * thousands of times, and the app locked up.
 */
const geometryCache = new WeakMap<Solid, Geometry>();

export function solidGeometry(solid: Solid): Geometry {
  const hit = geometryCache.get(solid);
  if (hit) return hit;
  const built = buildGeometry(solid);
  // FROZEN, because it is now shared. The old contract was "a fresh
  // object every call, so mutate it if you like"; nothing ever did, and
  // freezing turns the new contract from a promise into a guarantee.
  Object.freeze(built.vertices);
  Object.freeze(built.faces);
  Object.freeze(built);
  geometryCache.set(solid, built);
  return built;
}

/**
 * Tessellate a solid into vertices and outward-facing convex faces.
 *
 * The bottom ring comes first, then the top ring (or the single apex
 * vertex when `radiusTop` is zero), so face indices are predictable:
 * base vertex `i` is at `i`, its partner above at `sides + i`.
 */
function buildGeometry(solid: Solid): Geometry {
  const { sides, radiusTop, height, position } = solid;
  if (!Number.isFinite(sides) || sides < 3) {
    throw new Error(`A solid needs at least three sides, got ${sides}`);
  }
  const n = Math.floor(sides);
  const pointed = radiusTop < EPSILON;
  const vertices = solidVertices(solid, n, pointed);
  // Anywhere strictly inside the hull works as the reference; the axis at
  // mid-height always is.
  const interior: Vec3 = { x: position.x, y: (position.y + (position.y + height)) / 2, z: position.z };
  const faces = faceIndices(n, pointed).map(({ indices, kind }) => {
    const pts = indices.map((i) => vertices[i]);
    return { indices, normal: faceNormal(pts, interior), kind };
  });
  return { vertices, faces };
}

/** The bottom ring, then the top ring - or the apex, for a solid that comes to a point. */
function solidVertices(solid: Solid, n: number, pointed: boolean): Vec3[] {
  const { position } = solid;
  const placement: Placement = {
    rotationDeg: solid.rotation,
    centre: position,
    scaleX: solid.scaleX ?? 1,
    scaleZ: solid.scaleZ ?? 1,
  };
  const yTop = position.y + solid.height;
  const bottom = ring(n, solid.radiusBottom, position.y, placement);
  const top = pointed ? [{ x: position.x, y: yTop, z: position.z }] : ring(n, solid.radiusTop, yTop, placement);
  return [...bottom, ...top];
}

/**
 * Which vertices make each face of an n-sided solid: the sides, then the
 * bottom cap, then the top cap unless it comes to a point.
 *
 * A cone's sides are triangles sharing the apex; a prism's are quads.
 * Index order is reversed on the base so both caps wind the same way
 * when seen from outside; faceNormal fixes the sign either way, but a
 * consistent winding keeps the renderer's polygons non-self-crossing.
 */
function faceIndices(n: number, pointed: boolean): { indices: number[]; kind: FaceKind }[] {
  const sides = Array.from({ length: n }, (_, i) => {
    const j = (i + 1) % n;
    return { indices: pointed ? [i, j, n] : [i, j, n + j, n + i], kind: 'side' as FaceKind };
  });
  const bottom = { indices: Array.from({ length: n }, (_, i) => n - 1 - i), kind: 'bottom' as FaceKind };
  if (pointed) return [...sides, bottom];
  return [...sides, bottom, { indices: Array.from({ length: n }, (_, i) => n + i), kind: 'top' as FaceKind }];
}

export interface Box {
  min: Vec3;
  max: Vec3;
}

/** Axis-aligned extent - the cheap test that precedes every expensive one. */
/**
 * A solid's axis-aligned bounds, memoised like its geometry.
 *
 * The cheap rejection test that everything walking the map wants first:
 * a column outside these bounds cannot possibly be inside the solid, and
 * answering that costs six comparisons rather than a point-in-polyhedron
 * test against every face.
 */
const boundsCache = new WeakMap<Solid, Box>();

export function solidBounds(solid: Solid): Box {
  const hit = boundsCache.get(solid);
  if (hit) return hit;
  const box = boundingBox(solidGeometry(solid));
  boundsCache.set(solid, box);
  return box;
}

export function boundingBox(geometry: Pick<Geometry, 'vertices'>): Box {
  const vs = geometry.vertices;
  const min: Vec3 = { x: Infinity, y: Infinity, z: Infinity };
  const max: Vec3 = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const v of vs) {
    min.x = Math.min(min.x, v.x);
    min.y = Math.min(min.y, v.y);
    min.z = Math.min(min.z, v.z);
    max.x = Math.max(max.x, v.x);
    max.y = Math.max(max.y, v.y);
    max.z = Math.max(max.z, v.z);
  }
  return { min, max };
}

/**
 * Is every vertex on the inner side of every face plane?
 *
 * Line of sight assumes convexity, so this exists to ASSERT that
 * assumption - in property tests over the whole parameter space, and as a
 * guard if hand-authored geometry ever arrives.
 */
export function isConvex(geometry: Geometry, tolerance = 1e-6): boolean {
  for (const face of geometry.faces) {
    const p0 = geometry.vertices[face.indices[0]];
    for (const v of geometry.vertices) {
      if (dot(face.normal, sub(v, p0)) > tolerance) return false;
    }
  }
  return true;
}
